import { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import {
  BookmarkIcon,
  CalendarIcon,
  CheckIcon,
  CircleArrowRightIcon,
  HashIcon,
  LayoutGridIcon,
  MessagesSquareIcon,
  NetworkIcon,
  PinIcon,
  ThumbsUpIcon,
} from "@lucide/icons";
import { useQuery } from "@tanstack/react-query";

import { formatDate } from "simplydo/core/dates";
import { initUseQuery } from "simplydo/core/query";
import {
  LabelChooser,
  LabelStackView,
} from "simplydo/react/components/Choosers/LabelChooser/LabelChooser";
import { useThrottle } from "simplydo/react/hooks";
import { useTheme } from "simplydo/react/theme";
import { Button } from "simplydo/react/ui/button";
import { Card, CardContent } from "simplydo/react/ui/card";
import { H3, H4 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Label } from "simplydo/react/ui/label";
import { Popup } from "simplydo/react/ui/popup";
import { Progress } from "simplydo/react/ui/progress";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { useIdeaQueryParams } from "components/challenges/Challenge/Ideas/useIdeaQueryParams";
import { IdeaBusinessProfileChip, UserChip } from "components/lib/Chips";
import TagModalChooser, {
  TagView,
} from "components/lib/Choosers/Tags/TagModalChooser";
import { IdeaLikeHandler } from "components/lib/Ideas";
import { getImpactTypeIcon } from "components/lib/Ideas/ImpactStatements/impactIcons";
import PinIdeaHelper from "components/lib/Ideas/PinIdeaHelper";
import { ShadowedCard } from "components/lib/UI";

import { IdeaAssessmentProgress } from "../IdeaAssessmentProgress";
import IdeaDisplayFields from "../IdeaDisplayFields";
import { type IdeaCardViewProps, type IdeaDisplayField } from "../types";
import {
  addTag,
  labelIdea,
  pinIdea,
  removeTag,
  voteForIdea,
} from "./ideaViewActions";

const IdeaCardView = ({
  idea,
  challenge,
  onSelect,
  selectedIdeas,
  ideasVotedFor,
  setPreviewOpen,
}: IdeaCardViewProps) => {
  const user = useAppSelector((state) => state.user);
  const { t } = useTranslation();
  const theme = useTheme();

  const [tagsOpen, setTagsOpen] = useState(false);

  const projectManagement = idea.projectManagement;
  const pmBoard = projectManagement?.boards?.filter(
    (b: NonNullable<typeof projectManagement>["boards"][number]) =>
      b.forId === challenge?._id,
  )[0];
  const pmBoardEnabled = pmBoard?.isEnabled;

  const isSelected = selectedIdeas?.includes(idea._id ?? "");
  const completenessPercentage = Math.round((idea.completeness || 0) * 100);
  const ideaComplete = idea.completeness === 1;
  const usedVotes =
    parseInt(String(challenge.voteLimit ?? 0), 10) === ideasVotedFor;
  const showProgress = !!completenessPercentage && !idea.isSubmitted;

  const hasAssessments = useMemo(
    () =>
      (idea.assessments?.length ?? 0) > 0 ||
      (idea.invitedExternalAssessors?.length ?? 0) > 0,
    [idea],
  );

  const invitationQuery = useQuery(
    initUseQuery(api.invitations.getForType, ["ideaAssessor", idea.challenge]),
  );

  const pendingInvitations = invitationQuery.data?.invitations.filter((i) =>
    i?.forIdeas?.includes(idea._id),
  );
  const navigate = useNavigate();

  const openIdea = (options: { toComments?: boolean } = {}) => {
    if (setPreviewOpen) {
      setPreviewOpen(idea._id, { toComments: options?.toComments });
    } else {
      navigate(`/ideas/${idea._id}${options?.toComments ? "#comments" : ""}`);
    }
  };

  const { activityInfo = {}, createdAt = 0, updatedAt } = idea;

  const { lastActivityAt = updatedAt ?? createdAt } = activityInfo;

  const ideaOrder = useAppSelector((state) => state.challenges.ideaOrder);
  const ideaViewType = useAppSelector((state) => state.challenges.ideaViewType);
  const queryParams = useIdeaQueryParams(ideaViewType);
  const debouncedImpacts = useThrottle(api.challenges.getIdeaImpacts, 400, [], {
    useLeadingCall: true,
  });
  const ideaImpactsQuery = useQuery(
    initUseQuery(debouncedImpacts, [idea.challenge, queryParams], {
      select: (data) => data.ideaImpacts?.[idea._id],
      isEnabled: ideaOrder.startsWith("impact+"),
    }),
  );

  const activeImpactSort =
    !!challenge &&
    ideaImpactsQuery.data?.totalImpacts.find(
      (impact) => `impact+${impact.impactId}` === ideaOrder,
    );

  const displayFields: IdeaDisplayField[] = (
    challenge?.ownerIdeaTemplate?.body
      ?.filter((section) => section.type !== "assessment")
      .flatMap((section) =>
        section.fields.filter(
          (field) => field.isDisplayField && idea.templated?.[field.id],
        ),
      ) || []
  ).map((field) => ({
    id: field.id,
    value: idea.templated?.[field.id],
    title: field.title,
    type: field.type,
  }));

  const statusLabelStyle = {
    position: "absolute" as const,
    top: 5,
    right: 5,
    marginTop: 35,
  };

  return (
    <ShadowedCard>
      {showProgress ? (
        <Popup
          content={`${t("common:capitalise", { key: "generic.idea" })} is ${completenessPercentage}% complete`}
          trigger={
            <Progress
              size="large"
              style={{ height: "0.5rem", zIndex: 1 }}
              label={`${completenessPercentage}% complete`}
              indicating={!ideaComplete}
              color={ideaComplete ? "green" : "yellow"}
              percent={completenessPercentage}
              attached="top"
            />
          }
        />
      ) : null}
      <Link
        to={`/ideas/${idea._id}`}
        onClick={(e) => {
          if (onSelect) {
            onSelect(idea);
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        <div
          style={{
            width: "100%",
            height: "75px",
            position: "relative",
            backgroundImage: `url(${util.mixinCssUrlFallback(util.ideaCoverImage(idea, "640x100"), util.ideaCoverImage(idea))})`,
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center center",
            overflow: "hidden",
            ...(showProgress
              ? {}
              : { borderTopLeftRadius: 4, borderTopRightRadius: 4 }),
          }}
        >
          <div
            style={{
              position: "absolute",
              zIndex: 0,
              width: "100%",
              height: "100%",
              background: "rgba(0,0,0,0.2)",
            }}
          />
          <div style={{ position: "relative", zIndex: 1 }}>
            {idea.isSubmitted ? (
              <Label
                style={{ marginTop: 7 }}
                size="small"
                color="green"
                ribbon="right"
              >
                {t("ideas.card.submitted")}
              </Label>
            ) : (
              <Label
                style={{ marginTop: 7 }}
                size="small"
                color="yellow"
                ribbon="right"
              >
                {t("ideas.card.draft")}
              </Label>
            )}
          </div>
          {util.canManageChallenge(user, challenge) ||
          util.hasPermission(
            user,
            "challenge.editIdeaDeadlines",
            challenge?._id,
          ) ||
          util.hasPermission(user, "challenge.viewIdeas", challenge?._id) ? (
            <div style={statusLabelStyle}>
              {idea.submissionDeadline ? (
                <Popup
                  trigger={
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      color="secondary"
                    >
                      <Icon icon={CalendarIcon} />
                    </Button>
                  }
                  content={`This ${t("generic.idea")} has a custom deadline: ${formatDate(idea.submissionDeadline.deadline)}`}
                />
              ) : null}
              <LabelChooser
                trigger={
                  <Button
                    size="small"
                    color={
                      (idea.labels || []).length > 0 ? "secondary" : undefined
                    }
                  >
                    <Icon icon={BookmarkIcon} />
                  </Button>
                }
                tooltip="Add or remove labels"
                onComplete={(em) => labelIdea(idea, em, true)}
                existing={idea.labels}
                onRemoveExisting={(em) => labelIdea(idea, em, false)}
              />
              <Popup
                trigger={
                  <Button
                    size="small"
                    onClick={(e) => {
                      pinIdea(idea, !idea.isPinned);
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    color={idea.isPinned ? "secondary" : undefined}
                  >
                    <Icon icon={PinIcon} />
                  </Button>
                }
                content={
                  <div>
                    <p>
                      <b>
                        {idea.isPinned
                          ? t("ideas.options.pin.unPin")
                          : t("ideas.options.pin.challenge")}
                      </b>
                    </p>

                    <PinIdeaHelper challenge={challenge} />
                  </div>
                }
              />
              {onSelect ? (
                <Button
                  size="small"
                  onClick={(e) => {
                    onSelect(idea);
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  color={isSelected ? "secondary" : undefined}
                >
                  <Icon icon={CheckIcon} />
                </Button>
              ) : null}
            </div>
          ) : (
            <div style={statusLabelStyle}>
              {idea.isPinned ? (
                <Button size="small" color="secondary">
                  <Icon icon={PinIcon} />
                </Button>
              ) : null}
              {onSelect ? (
                <Button
                  size="small"
                  onClick={(e) => {
                    onSelect(idea);
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  color={isSelected ? "secondary" : undefined}
                >
                  <Icon icon={CheckIcon} />
                </Button>
              ) : null}
            </div>
          )}
        </div>
      </Link>

      <CardContent>
        <H3 style={{ color: "#4d4949" }}>
          <span onClick={() => openIdea()} style={{ cursor: "pointer" }}>
            {idea.children ? <Icon icon={NetworkIcon} size="small" /> : null}
            {idea.name}
          </span>
        </H3>
        {idea.ownerChallenge && (
          <div style={{ marginBottom: 5 }}>
            <Link
              style={{ textDecorationLine: "underline" }}
              to={`/challenges/${idea.ownerChallenge._id}`}
            >
              {idea.ownerChallenge?.name}
            </Link>
          </div>
        )}

        {idea?.owner ? (
          <div>
            <span
              style={{
                display: "flex",
                color: "gray",
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
                maxWidth: "100%",
              }}
            >
              {idea.lockedOwnerIdeaBusinessProfile ? (
                <IdeaBusinessProfileChip
                  ideaBusinessProfile={idea.lockedOwnerIdeaBusinessProfile}
                  compact="very"
                />
              ) : (
                <UserChip user={idea.owner} compact="very" />
              )}
              {(idea.collaborators?.length ||
                !!idea.lockedOwnerIdeaBusinessProfile?.name) &&
              idea.authors ? (
                <>
                  &nbsp;{"+"}&nbsp;
                  <Popup
                    on="click"
                    trigger={
                      <span
                        style={{
                          cursor: "pointer",
                          userSelect: "none",
                          textDecoration: "underline",
                        }}
                        onClick={(e) => e.preventDefault()}
                      >
                        {util.pluralise(
                          idea.lockedOwnerIdeaBusinessProfile?.name
                            ? (idea.collaborators?.length || 0) + 1
                            : (idea.collaborators?.length ?? 0),
                          "other",
                          "others",
                        )}
                      </span>
                    }
                    content={
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 5,
                        }}
                      >
                        {idea.authors
                          ?.filter(
                            (a) =>
                              !!idea.lockedOwnerIdeaBusinessProfile?.name ||
                              a._id !== idea.user,
                          )
                          .map((a) => (
                            <UserChip key={a._id} user={a} />
                          ))}
                      </div>
                    }
                  />
                </>
              ) : null}
            </span>
            <p style={{ marginTop: 4, fontSize: 13, color: "grey" }}>
              {idea.isSubmitted
                ? `${t("ideas.card.submittedOn", { date: formatDate(idea.submittedAt, "dd/MM/yy") })}`
                : `${t("ideas.card.updatedAt", { date: formatDate(idea.updatedAt, "dd/MM/yy") })}`}
              , last activity on {formatDate(lastActivityAt, "dd/MM/yy")}.
            </p>
          </div>
        ) : null}

        {(util.canManageChallenge(user, challenge) ||
          util.hasPermission(
            user,
            "challenge.viewProjectBoard",
            challenge?._id,
          )) &&
          pmBoardEnabled &&
          pmBoard?.laneObject && (
            <div style={{ marginTop: 5, display: "inline-flex" }}>
              <Icon style={{ color: "#aab6b2" }} icon={LayoutGridIcon} />
              <p style={{ color: "#aab6b2" }}>{pmBoard.laneObject?.name}</p>
            </div>
          )}

        <div
          style={{
            display: "flex",
            maxWidth: "100%",
            flexWrap: "wrap",
            marginTop: 5,
          }}
        >
          {(util.canManageChallenge(user, challenge) ||
            util.hasPermission(
              user,
              "challenge.viewAssessments",
              challenge?._id,
            )) &&
          hasAssessments ? (
            <IdeaAssessmentProgress idea={idea} challenge={challenge} compact />
          ) : null}
          {idea.labels ? (
            <LabelStackView labels={idea.labels} horizontal />
          ) : null}
        </div>
        {displayFields.length > 0 ? (
          <div style={{ marginTop: 8 }}>
            <IdeaDisplayFields fields={displayFields} />
          </div>
        ) : null}

        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "flex-start",
            width: "100%",
            marginTop: 10,
          }}
        >
          {activeImpactSort?.ownerImpact ? (
            <Button
              size="small"
              color="purple"
              label={
                <Label basic pointing="left" color="purple">
                  {activeImpactSort.annualisedValue ??
                    activeImpactSort.choice?.value ??
                    activeImpactSort.value}
                </Label>
              }
              style={{ marginLeft: 14 }}
            >
              <Icon
                icon={getImpactTypeIcon(activeImpactSort.ownerImpact?.type)}
              />
            </Button>
          ) : null}

          <TagModalChooser
            hideView={true}
            isOpen={tagsOpen}
            onClose={() => setTagsOpen(false)}
            tags={idea.ownerTags ?? []}
            availableTags={
              challenge.tagList?.filter(
                (tag) => !tag?.disabledEntities?.includes("ideas"),
              ) ?? []
            }
            addTag={(tag) => addTag(idea, tag)}
            removeTag={(tag) => removeTag(idea, tag)}
            canEdit={
              util.canManageChallenge(user, challenge) ||
              util.hasPermission(user, "challenge.editIdeaTags", challenge?._id)
            }
          />

          {(challenge?.tagList?.length ?? 0) > 0 && (
            <Popup
              on="click"
              trigger={
                <Button
                  size="small"
                  label={
                    <Label basic pointing="left">
                      {idea.tags?.length ?? 0}
                    </Label>
                  }
                >
                  <Icon icon={HashIcon} />
                </Button>
              }
              content={
                <div>
                  <H4>Tags</H4>
                  <TagView tags={idea.ownerTags || []} />
                  {util.canManageChallenge(user, challenge) ||
                  util.hasPermission(
                    user,
                    "challenge.editIdeaTags",
                    challenge?._id,
                  ) ? (
                    <Button
                      size="small"
                      style={{ marginLeft: 3 }}
                      onClick={() => {
                        setTagsOpen(true);
                      }}
                    >
                      <Icon icon={HashIcon} />
                      {t("tags.edit")}
                    </Button>
                  ) : null}
                </div>
              }
            />
          )}
          {!challenge?.preventIdeaComments && (
            <Button
              onClick={() => {
                openIdea({ toComments: true });
              }}
              color={idea.commentCount ? "blue" : "grey"}
              size="small"
              label={
                <Label basic pointing="left">
                  {idea.commentCount || 0}
                </Label>
              }
              style={{ marginLeft: 5 }}
            >
              <Icon icon={MessagesSquareIcon} />
            </Button>
          )}

          {challenge.votingVisibility === "users" ? (
            <IdeaLikeHandler
              idea={idea}
              challenge={challenge}
              onLikeIdea={(ideaId, addVote) => voteForIdea(idea, addVote)}
              trigger={
                <Button
                  disabled={usedVotes && !idea.votedFor}
                  color={idea.votedFor ? "green" : "grey"}
                  style={{ marginLeft: 5 }}
                  size="small"
                  label={
                    util.hasPermission(
                      user,
                      "challenge.viewIdeas",
                      challenge?._id,
                    ) ||
                    util.canManageChallenge(user, challenge) ||
                    challenge.voteCountVisibility === "users" ? (
                      <Label basic pointing="left">
                        {idea.voteCount || 0}
                      </Label>
                    ) : null
                  }
                >
                  <Icon icon={ThumbsUpIcon} />
                </Button>
              }
            />
          ) : null}
        </div>
      </CardContent>

      <div
        onClick={() => openIdea()}
        tabIndex={0}
        role="button"
        style={{ cursor: "pointer" }}
      >
        <CardContent
          extra
          style={{
            backgroundColor: theme.primaryColour,
            textAlign: "center",
            fontWeight: 600,
            maxHeight: 50,
            color: "#fff",
            textTransform: "uppercase",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.3rem",
          }}
        >
          {t("ideas.card.view")} <Icon icon={CircleArrowRightIcon} />
        </CardContent>
      </div>
    </ShadowedCard>
  );
};

export default IdeaCardView;
